import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cargoService, Cargo, CargoRequest } from '../../services/cargo';

interface CargoFormProps {
  isEdit?: boolean;
}

const CargoForm: React.FC<CargoFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [descricao, setDescricao] = React.useState('');
  const [errors, setErrors] = React.useState({ descricao: '' });

  // Buscar cargo para edição
  const { data: cargo, isLoading } = useQuery({
    queryKey: ['cargo', id],
    queryFn: () => cargoService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: CargoRequest) => cargoService.save(data),
    onSuccess: () => {
      // Invalidar o cache dos cargos para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['cargos'] });
      navigate('/Cargo');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar cargo:', error);
    },
  });

  useEffect(() => {
    if (cargo && isEdit) {
      setDescricao(cargo.descricao);
    }
  }, [cargo, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = { descricao: '' };
    let isValid = true;

    if (!descricao.trim()) {
      newErrors.descricao = 'Cargo é obrigatório';
      isValid = false;
    } else if (descricao.length > 30) {
      newErrors.descricao = 'Cargo não deve possuir mais que 30 caracteres';
      isValid = false;
    } else if (descricao.length < 3) {
      newErrors.descricao = 'Cargo não deve possuir menos que 3 caracteres';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const cargoData: CargoRequest = {
        id: isEdit ? Number(id) : undefined,
        descricao: descricao.trim(),
      };
      
      saveMutation.mutate(cargoData);
    }
  };

  const handleClear = () => {
    setDescricao('');
    setErrors({ descricao: '' });
  };

  const handleBack = () => {
    navigate('/cargos');
  };

  if (isEdit && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Cargo"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            error={!!errors.descricao}
            helperText={errors.descricao}
            slotProps={{
              htmlInput: { maxLength: 30 }
            }}
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CargoForm;
